import { useEffect, useMemo, useRef, useState } from "react";
import { MessageSquare, X, Send, User, Search, Loader2, HelpCircle } from "lucide-react";
import {
  assistantPublicHistory, assistantPublicLead,
  assistantAdminHistory, assistantStatus,
} from "@/lib/api";
import { streamSSE } from "@/lib/sse";
import { toast } from "sonner";
import { useT } from "@/lib/t";
import { useLang } from "@/context/LangContext";

/**
 * Assistant surfaces for the Colorado Catholic Conference.
 *
 * Two exports:
 *   1. <InlineAssistant/>  — embedded, native-looking research tool for public
 *      pages (Bill Tracker, Priority Detail). It reads as "Ask about a bill"
 *      or "Search the record", NOT a chatbot. No floating bubble, no sparkles,
 *      no "AI" branding — the aesthetic is a Reference Librarian, not
 *      Software.
 *   2. <AssistantWidget mode="admin"/> — internal CRM Copilot drawer. Staff-
 *      only. Kept as a compact side panel with a plain speech-bubble icon
 *      (no sparkles, no "AI" wording).
 *
 * Session persists in localStorage per persona so a visitor's conversation
 * survives page navigation. The public inline surface also unlocks an
 * inline sign-up card after the 2nd turn.
 */

// ────────────────────────────────────────────────────────────
// Shared plumbing
// ────────────────────────────────────────────────────────────
function usePersistedSession(storageKey) {
  return useMemo(() => {
    let s = typeof window !== "undefined" ? localStorage.getItem(storageKey) : null;
    if (!s) {
      s = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      if (typeof window !== "undefined") localStorage.setItem(storageKey, s);
    }
    return s;
  }, [storageKey]);
}

/**
 * Minimal safe markdown-lite renderer. Supports [text](url) + **bold**.
 * Never sets innerHTML. URLs validated to be relative (`/`) or http(s)/mailto.
 */
function AssistantMarkdown({ text }) {
  if (!text) return null;
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let lastIdx = 0;
  let m;
  while ((m = linkRe.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push({ type: "text", value: text.slice(lastIdx, m.index) });
    parts.push({ type: "link", label: m[1], url: m[2] });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push({ type: "text", value: text.slice(lastIdx) });

  const renderBold = (str) => {
    const chunks = String(str).split(/(\*\*[^*]+\*\*)/g);
    return chunks.map((c, i) =>
      c.startsWith("**") && c.endsWith("**")
        ? <strong key={i}>{c.slice(2, -2)}</strong>
        : <span key={i}>{c}</span>
    );
  };

  const safeUrl = (u) => {
    if (!u) return "#";
    if (u.startsWith("/")) return u;
    if (u.startsWith("http://") || u.startsWith("https://") || u.startsWith("mailto:")) return u;
    return "#";
  };

  return (
    <>
      {parts.map((p, i) =>
        p.type === "link"
          ? (
            <a
              key={i}
              href={safeUrl(p.url)}
              target={safeUrl(p.url).startsWith("http") ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="text-ccc-plum underline decoration-ccc-gold underline-offset-2 hover:text-ccc-plum-deep font-semibold"
              data-testid="assistant-link"
            >
              {p.label}
            </a>
          )
          : <span key={i}>{renderBold(p.value)}</span>
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────
// InlineAssistant — public, embedded research tool
// ────────────────────────────────────────────────────────────

/**
 * <InlineAssistant/> — an in-page reference tool.
 *
 * @param {"bill" | "record"} tone
 *   `"bill"`   → labeled "Ask about a bill" (used on the Bill Tracker &
 *                individual bill pages).
 *   `"record"` → labeled "Search the record" (used on Priority pages).
 *
 * @param {string[]} [suggestions]
 *   Optional pre-filled prompts staff can seed for the page context. If
 *   omitted, sensible defaults for the tone are used.
 *
 * @param {string} [context]
 *   Optional extra sentence appended to the user's first message so the
 *   assistant knows what the visitor was reading (e.g. "Bill HB25-1010").
 */
export function InlineAssistant({ tone = "record", suggestions, context, className = "" }) {
  const t = useT();
  const { lang } = useLang();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [greeting, setGreeting] = useState("");
  const [showLeadCard, setShowLeadCard] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const scrollRef = useRef(null);

  const storageKey = `ccc_ai_session_public_${tone}`;
  const tokenStorageKey = `ccc_ai_session_public_${tone}_token`;
  const sessionId = usePersistedSession(storageKey);

  const isBill = tone === "bill";
  const title = isBill ? t("assistant.title_bill") : t("assistant.title_record");
  const eyebrow = isBill ? t("assistant.eyebrow_bill") : t("assistant.eyebrow_record");
  const placeholder = isBill ? t("assistant.placeholder_bill") : t("assistant.placeholder_record");
  const description = isBill ? t("assistant.description_bill") : t("assistant.description_record");

  const defaultSuggestions = isBill ? t("assistant.suggestions_bill") : t("assistant.suggestions_record");
  const seedSuggestions = suggestions && suggestions.length ? suggestions : defaultSuggestions;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await assistantStatus(lang);
        if (!cancelled) {
          setConfigured(s.configured);
          setGreeting(s.greeting);
        }
      } catch { /* silent */ }
      try {
        const savedToken = typeof window !== "undefined" ? localStorage.getItem(tokenStorageKey) : null;
        if (!savedToken) return;
        const h = await assistantPublicHistory(sessionId, savedToken);
        if (!cancelled && h.messages && h.messages.length) setMessages(h.messages);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [sessionId, lang]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  useEffect(() => {
    if (leadDone) return;
    const userTurns = messages.filter(m => m.role === "user").length;
    if (userTurns >= 2) setShowLeadCard(true);
  }, [messages, leadDone]);

  const doSend = async (raw) => {
    const text = String(raw || "").trim();
    if (!text || sending) return;
    setInput("");
    const outbound = context ? `${text}\n\n(Context: ${context})` : text;
    setMessages(prev => [...prev, { role: "user", content: text, created_at: new Date().toISOString() }]);
    setSending(true);
    const asstIndex = { current: null };
    setMessages(prev => {
      asstIndex.current = prev.length;
      return [...prev, { role: "assistant", content: "", created_at: new Date().toISOString(), streaming: true }];
    });
    let assembled = "";
    try {
      for await (const frame of streamSSE("/public/assistant/stream", { session_id: sessionId, message: outbound })) {
        if (frame.event === "delta") {
          assembled += frame.data;
          setMessages(prev => {
            const next = prev.slice();
            const i = asstIndex.current;
            if (i != null && next[i]) next[i] = { ...next[i], content: assembled };
            return next;
          });
        } else if (frame.event === "done") {
          try {
            const meta = JSON.parse(frame.data);
            if (meta.session_token && typeof window !== "undefined") {
              localStorage.setItem(tokenStorageKey, meta.session_token);
            }
            if (typeof meta.configured === "boolean") setConfigured(meta.configured);
          } catch { /* frame data not JSON — ignore */ }
        }
      }
      setMessages(prev => {
        const next = prev.slice();
        const i = asstIndex.current;
        if (i != null && next[i]) next[i] = { ...next[i], streaming: false };
        return next;
      });
    } catch (err) {
      const msg = err?.status === 429
        ? t("assistant.rate_limited")
        : t("assistant.network_error");
      setMessages(prev => {
        const next = prev.slice();
        const i = asstIndex.current;
        if (i != null && next[i]) next[i] = { role: "assistant", content: msg, created_at: new Date().toISOString() };
        return next;
      });
    } finally {
      setSending(false);
    }
  };

  const send = (e) => { e?.preventDefault(); doSend(input); };

  return (
    <section
      className={`bg-ccc-white border border-ccc-rule ${className}`}
      data-testid="inline-assistant"
      aria-label={title}
    >
      <header className="px-6 py-5 border-b border-ccc-rule">
        <span className="gold-rule mb-3" aria-hidden="true" />
        <div className="eyebrow mb-1">{eyebrow}</div>
        <h3 className="font-serif font-bold text-ccc-ink" style={{ fontSize: "24px" }}>
          {title}
        </h3>
        <p className="text-ccc-body mt-2" style={{ fontSize: "17px", lineHeight: 1.6 }}>
          {description}
        </p>
      </header>

      <div
        ref={scrollRef}
        className="px-6 py-5 space-y-4 max-h-[420px] overflow-y-auto bg-ccc-paper/40"
        data-testid="inline-assistant-messages"
      >
        {messages.length === 0 && (
          <div>
            {greeting && (
              <p className="font-serif italic text-ccc-body mb-4"
                 style={{ fontSize: "18px", lineHeight: 1.65 }}>
                {greeting}
              </p>
            )}
            <div className="space-y-2">
              <div className="eyebrow mb-2" style={{ fontSize: "13px" }}>{t("assistant.try_one")}</div>
              {seedSuggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => doSend(s)}
                  data-testid={`inline-assistant-suggestion-${i}`}
                  className="block w-full text-left px-4 py-3 bg-ccc-white border border-ccc-rule hover:border-ccc-plum text-ccc-body transition-colors"
                  style={{ fontSize: "17px" }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}
            data-testid={`inline-assistant-msg-${m.role}-${i}`}
          >
            <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-ccc-white border border-ccc-rule">
              {m.role === "user" ? <User size={14} className="text-ccc-plum"/> : <HelpCircle size={14} className="text-ccc-plum"/>}
            </div>
            <div
              className={`px-4 py-3 max-w-[80%] whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-ccc-plum text-ccc-paper"
                  : "bg-ccc-white border border-ccc-rule text-ccc-ink"
              }`}
              style={{ fontSize: "17px", lineHeight: 1.6 }}
            >
              {m.role === "assistant" ? <AssistantMarkdown text={m.content}/> : m.content}
            </div>
          </div>
        ))}

        {sending && messages.at(-1)?.role !== "assistant" && (
          <div className="flex gap-3" data-testid="inline-assistant-typing">
            <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-ccc-white border border-ccc-rule">
              <HelpCircle size={14} className="text-ccc-plum"/>
            </div>
            <div className="bg-ccc-white border border-ccc-rule px-4 py-3">
              <Loader2 size={16} className="animate-spin text-ccc-muted"/>
            </div>
          </div>
        )}

        {!configured && (
          <div className="text-ccc-body bg-ccc-paper border-l-2 border-ccc-gold px-4 py-3"
               style={{ fontSize: "16px" }}
               data-testid="inline-assistant-offline">
            {t("assistant.offline")}
          </div>
        )}

        {showLeadCard && !leadDone && (
          <LeadCaptureCard
            sessionId={sessionId}
            onDone={() => { setLeadDone(true); toast.success(t("assistant.stay_in_loop")); }}
            onDismiss={() => setShowLeadCard(false)}
          />
        )}
      </div>

      <form onSubmit={send} className="px-6 py-4 border-t border-ccc-rule bg-ccc-white flex gap-3 items-center">
        <Search size={16} className="text-ccc-muted shrink-0" aria-hidden="true"/>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          disabled={sending}
          data-testid="inline-assistant-input"
          className="flex-1 bg-transparent border-0 focus:outline-none text-ccc-ink placeholder:text-ccc-muted"
          style={{ fontSize: "18px" }}
          maxLength={2000}
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          data-testid="inline-assistant-send"
          className="bg-ccc-plum text-ccc-paper px-5 py-2 font-semibold uppercase tracking-[0.08em] hover:bg-ccc-plum-deep transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ fontSize: "15px" }}
        >
          {sending ? <Loader2 size={16} className="animate-spin"/> : t("assistant.send")}
        </button>
      </form>
    </section>
  );
}

// ────────────────────────────────────────────────────────────
// AssistantWidget — admin-only drawer
// ────────────────────────────────────────────────────────────

/**
 * <AssistantWidget mode="admin"/> — CRM Copilot drawer for staff.
 *
 * Public mode (`mode="public"`) is intentionally a no-op — the public site
 * embeds <InlineAssistant/> directly into the Bill Tracker and Priority
 * pages instead of using a floating bubble.
 */
export function AssistantWidget({ mode = "admin" }) {
  // Public mode is embedded via <InlineAssistant/> on specific pages. The
  // floating bubble is retired for public visitors per the CCC design brief.
  if (mode !== "admin") return null;

  return <AdminAssistantDrawer />;
}

function AdminAssistantDrawer() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const storageKey = "ccc_ai_session_admin";
  const sessionId = usePersistedSession(storageKey);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const h = await assistantAdminHistory(sessionId);
        if (!cancelled && h.messages && h.messages.length) setMessages(h.messages);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open, sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: text, created_at: new Date().toISOString() }]);
    setSending(true);
    const asstIndex = { current: null };
    setMessages(prev => {
      asstIndex.current = prev.length;
      return [...prev, { role: "assistant", content: "", created_at: new Date().toISOString(), streaming: true }];
    });
    let assembled = "";
    try {
      for await (const frame of streamSSE("/admin/assistant/stream", { session_id: sessionId, message: text })) {
        if (frame.event === "delta") {
          assembled += frame.data;
          setMessages(prev => {
            const next = prev.slice();
            const i = asstIndex.current;
            if (i != null && next[i]) next[i] = { ...next[i], content: assembled };
            return next;
          });
        }
      }
      setMessages(prev => {
        const next = prev.slice();
        const i = asstIndex.current;
        if (i != null && next[i]) next[i] = { ...next[i], streaming: false };
        return next;
      });
    } catch (err) {
      const msg = err?.status === 429
        ? "Rate limited — wait a moment and try again."
        : "Copilot couldn't respond. Please try again.";
      setMessages(prev => {
        const next = prev.slice();
        const i = asstIndex.current;
        if (i != null && next[i]) next[i] = { role: "assistant", content: msg, created_at: new Date().toISOString() };
        return next;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open CRM Copilot"
          data-testid="assistant-fab-admin"
          className="fixed bottom-5 right-5 z-40 bg-ccc-plum text-ccc-paper shadow-md hover:shadow-lg w-12 h-12 flex items-center justify-center transition-colors hover:bg-ccc-plum-deep border border-ccc-gold-light"
        >
          <MessageSquare size={18} />
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-5 right-5 z-40 bg-ccc-white border border-ccc-rule-strong flex flex-col w-[calc(100vw-40px)] sm:w-[420px] h-[600px] max-h-[85vh] shadow-xl"
          data-testid="assistant-drawer-admin"
        >
          <header className="bg-ccc-plum text-ccc-paper px-5 py-4 flex justify-between items-center border-b border-ccc-plum-deep">
            <div>
              <div className="font-serif font-semibold leading-tight" style={{ fontSize: "20px" }}>
                CRM Copilot
              </div>
              <div className="uppercase tracking-[0.14em] text-ccc-gold-light" style={{ fontSize: "12px" }}>
                Data · Drafts · Tactical prompts
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close copilot"
              data-testid="assistant-close-admin"
              className="text-ccc-paper hover:text-ccc-gold-light p-1"
            >
              <X size={18}/>
            </button>
          </header>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-ccc-paper/40"
            data-testid="assistant-messages-admin"
          >
            {messages.length === 0 && (
              <div className="bg-ccc-white border border-ccc-rule px-4 py-3 font-serif italic text-ccc-body"
                   style={{ fontSize: "17px", lineHeight: 1.6 }}>
                CRM Copilot. Try:<br/>
                &middot; "What's overdue this week?"<br/>
                &middot; "Draft a follow-up to newsletter signups from last week"<br/>
                &middot; "Where do I edit the announcement bar?"
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}
                data-testid={`assistant-msg-${m.role}-${i}`}
              >
                <div className="w-7 h-7 shrink-0 flex items-center justify-center bg-ccc-white border border-ccc-rule">
                  {m.role === "user" ? <User size={13} className="text-ccc-plum"/> : <MessageSquare size={13} className="text-ccc-plum"/>}
                </div>
                <div
                  className={`px-3 py-2 max-w-[80%] whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-ccc-plum text-ccc-paper"
                      : "bg-ccc-white border border-ccc-rule text-ccc-ink"
                  }`}
                  style={{ fontSize: "16px", lineHeight: 1.55 }}
                >
                  {m.role === "assistant" ? <AssistantMarkdown text={m.content}/> : m.content}
                </div>
              </div>
            ))}
            {sending && messages.at(-1)?.role !== "assistant" && (
              <div className="flex gap-2" data-testid="assistant-typing-admin">
                <div className="w-7 h-7 shrink-0 flex items-center justify-center bg-ccc-white border border-ccc-rule">
                  <MessageSquare size={13} className="text-ccc-plum"/>
                </div>
                <div className="bg-ccc-white border border-ccc-rule px-3 py-2">
                  <Loader2 size={14} className="animate-spin text-ccc-muted"/>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="px-4 py-3 border-t border-ccc-rule bg-ccc-white flex gap-2 items-center">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask your copilot…"
              disabled={sending}
              data-testid="assistant-input-admin"
              className="flex-1 px-3 py-2 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
              style={{ fontSize: "16px" }}
              maxLength={4000}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="assistant-send-admin"
              className="bg-ccc-plum hover:bg-ccc-plum-deep text-ccc-paper px-3 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Send"
            >
              {sending ? <Loader2 size={16} className="animate-spin"/> : <Send size={16}/>}
            </button>
          </form>
        </div>
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────
// LeadCaptureCard — used by InlineAssistant after 2 turns
// ────────────────────────────────────────────────────────────
function LeadCaptureCard({ sessionId, onDone, onDismiss }) {
  const t = useT();
  const [form, setForm] = useState({ name: "", email: "", parish: "", interests: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error(`${t("assistant.name_placeholder")} + ${t("assistant.email_placeholder")}`);
    setSaving(true);
    try {
      await assistantPublicLead({ session_id: sessionId, ...form });
      onDone();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) toast.error(t("assistant.rate_limited"));
      else toast.error(err?.response?.data?.detail || t("assistant.network_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="bg-ccc-white border-l-4 border-ccc-gold border-t border-r border-b border-ccc-rule p-4 space-y-3"
      data-testid="assistant-lead-card"
    >
      <div className="flex justify-between items-start gap-3">
        <div>
          <div className="eyebrow" style={{ fontSize: "13px" }}>{t("assistant.stay_in_loop")}</div>
          <div className="font-serif font-semibold text-ccc-ink mt-1" style={{ fontSize: "20px", lineHeight: 1.25 }}>
            {t("assistant.updates_prompt")}
          </div>
        </div>
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="text-ccc-muted hover:text-ccc-ink">
          <X size={16}/>
        </button>
      </div>
      <input required type="text" placeholder={t("assistant.name_placeholder")} value={form.name}
        onChange={e => setForm({ ...form, name: e.target.value })}
        data-testid="lead-name"
        className="w-full px-3 py-2 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
        style={{ fontSize: "17px" }}/>
      <input required type="email" placeholder={t("assistant.email_placeholder")} value={form.email}
        onChange={e => setForm({ ...form, email: e.target.value })}
        data-testid="lead-email"
        className="w-full px-3 py-2 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
        style={{ fontSize: "17px" }}/>
      <input type="text" placeholder={t("assistant.parish_placeholder")} value={form.parish}
        onChange={e => setForm({ ...form, parish: e.target.value })}
        data-testid="lead-parish"
        className="w-full px-3 py-2 border border-ccc-rule focus:outline-none focus:border-ccc-plum"
        style={{ fontSize: "17px" }}/>
      <button
        type="submit"
        disabled={saving}
        data-testid="lead-submit"
        className="w-full bg-ccc-plum text-ccc-paper py-2.5 uppercase tracking-[0.08em] font-semibold hover:bg-ccc-plum-deep disabled:opacity-50"
        style={{ fontSize: "15px" }}
      >
        {saving ? t("assistant.signing_up") : t("assistant.sign_up")}
      </button>
    </form>
  );
}
